import React, { useEffect, useState } from 'react';
import {
  deriveCryptographicMeshId,
  generateUUIDv7,
  GenesisConfiguration,
  GenesisProvisioner,
} from 'mesh-microkernel';
import { THEME_PRESETS, ThemePreset } from '../theme/themeEngine';

export interface UserProfileData {
  displayName: string;
  hnsAlias: string;
  avatarIcon: string;
  avatarUrl?: string;
  bio: string;
  securityTier: 'CLASSICAL' | 'QUANTUM_RESISTANT';
}

interface OnboardingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaveProfile: (profile: UserProfileData) => void;
  initialProfile: UserProfileData;
  isGenesisWizard?: boolean;
  initialModalMode?: 'gateway' | 'wizard';
  existingGenesis?: GenesisConfiguration | null;
  onCompleteGenesis?: (config: GenesisConfiguration) => void;
  onCryptographicReset?: () => void;
  nodeId?: string;
  meshSuffix?: string;
}

export const OnboardingModal: React.FC<OnboardingModalProps> = ({
  isOpen,
  onClose,
  onSaveProfile,
  initialProfile,
  isGenesisWizard = false,
  initialModalMode,
  existingGenesis = null,
  onCompleteGenesis,
  onCryptographicReset,
  nodeId,
  meshSuffix = 'mesh',
}) => {
  const [modalMode, setModalMode] = useState<'gateway' | 'wizard' | 'helper'>('gateway');
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [profile, setProfile] = useState<UserProfileData>(initialProfile);
  const [migrationFileNotice, setMigrationFileNotice] = useState<string | null>(null);

  // Helper Enrollment State
  const [helperInvitationString, setHelperInvitationString] = useState<string>('');
  const [helperParsedInvitation, setHelperParsedInvitation] = useState<any | null>(null);
  const [helperRamGb, setHelperRamGb] = useState<number>(4);
  const [helperStorageGb, setHelperStorageGb] = useState<number>(20);
  const [helperEnableRelay, setHelperEnableRelay] = useState<boolean>(true);
  const [helperEnableGpu, setHelperEnableGpu] = useState<boolean>(true);
  const [helperEnablePrinter, setHelperEnablePrinter] = useState<boolean>(true);
  const [helperEnableBle, setHelperEnableBle] = useState<boolean>(true);
  const [helperEnableMedia, setHelperEnableMedia] = useState<boolean>(false);
  const [helperNotice, setHelperNotice] = useState<string | null>(null);
  const [helperEnrolled, setHelperEnrolled] = useState<boolean>(false);

  const [ownerUuidv7, setOwnerUuidv7] = useState<string>(() => {
    if (existingGenesis?.owner?.ownerId) return existingGenesis.owner.ownerId;
    if (typeof window !== 'undefined') {
      const savedOwner = localStorage.getItem('sovereign_owner_id');
      if (savedOwner) return savedOwner;
    }
    return nodeId || generateUUIDv7();
  });
  const [meshName, setMeshName] = useState<string>(() => existingGenesis?.meshName || 'sovereign-mesh');
  const [ownerAlias, setOwnerAlias] = useState<string>(() => {
    if (existingGenesis?.hns?.owner?.name) return existingGenesis.hns.owner.name;
    if (initialProfile?.displayName && initialProfile.displayName !== 'Sovereign Admin' && !initialProfile.displayName.startsWith('Peer ')) {
      return initialProfile.displayName;
    }
    if (typeof window !== 'undefined') {
      const savedAlias = localStorage.getItem('sovereign_owner_alias');
      if (savedAlias) return savedAlias;
    }
    return initialProfile?.displayName || 'Sovereign Admin';
  });
  const [mission, setMission] = useState<string>(
    () =>
      existingGenesis?.mission ||
      'Decentralized peer-to-peer collaboration network with zero server dependencies and post-quantum encryption.'
  );
  const [workspaceTheme, setWorkspaceTheme] = useState<ThemePreset>('midnight');

  useEffect(() => {
    if (isOpen) {
      if (initialModalMode) {
        setModalMode(initialModalMode);
      } else {
        setModalMode(isGenesisWizard ? 'gateway' : 'wizard');
      }
      setStep(1);
      setHelperEnrolled(false);
      setHelperNotice(null);

      // Load existing details for profile
      if (initialProfile) {
        setProfile(initialProfile);
        if (initialProfile.displayName) {
          setOwnerAlias(initialProfile.displayName);
        }
      }

      // Enforce read-only HNS alias for ordinary peers on current connected mesh
      if (!isGenesisWizard && nodeId) {
        setProfile((prev) => {
          const nameSlug = (prev.displayName || nodeId).toLowerCase().replace(/[^a-z0-9-]/g, '');
          return {
            ...prev,
            hnsAlias: `hns://${nameSlug}.${meshSuffix}`,
          };
        });
      }

      if (isGenesisWizard) {
        if (existingGenesis) {
          setOwnerUuidv7(existingGenesis.owner.ownerId);
          setMeshName(existingGenesis.meshName);
          setMission(existingGenesis.mission);
          if (existingGenesis.hns?.owner?.name) {
            setOwnerAlias(existingGenesis.hns.owner.name);
          }
        } else {
          const savedOwnerId = typeof window !== 'undefined' ? localStorage.getItem('sovereign_owner_id') : null;
          if (savedOwnerId) {
            setOwnerUuidv7(savedOwnerId);
          } else if (nodeId) {
            setOwnerUuidv7(nodeId);
          }
        }
      }
    }
  }, [isOpen, existingGenesis, isGenesisWizard, initialModalMode, initialProfile, nodeId, meshSuffix]);

  if (!isOpen) return null;

  const handleSelectFactoryReset = () => {
    try {
      localStorage.clear();
      sessionStorage.clear();
    } catch {}
    if (onCryptographicReset) {
      onCryptographicReset();
    }
    const freshUuid = generateUUIDv7();
    setOwnerUuidv7(freshUuid);
    setOwnerAlias('Sovereign Admin');
    setProfile({
      displayName: isGenesisWizard ? 'Sovereign Admin' : 'New Peer',
      hnsAlias: `hns://peer.${meshSuffix}`,
      avatarIcon: isGenesisWizard ? '🛡️' : '👤',
      bio: isGenesisWizard ? 'Sovereign Mesh Box Owner' : 'Sovereign Mesh Peer',
      securityTier: isGenesisWizard ? 'QUANTUM_RESISTANT' : 'CLASSICAL',
    });
    setModalMode('wizard');
    setStep(1);
  };

  const handleSelectContinue = () => {
    // Preserve existing UUIDv7 and pre-fill existing alias/profile
    if (isGenesisWizard) {
      setProfile((prev) => ({
        ...prev,
        displayName: ownerAlias || prev.displayName,
      }));
    }
    setModalMode('wizard');
    setStep(1);
  };

  const handleRegisterNewProfile = () => {
    setModalMode('wizard');
    setStep(1);
  };

  const handleImportMigrationFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const parsed = JSON.parse(event.target?.result as string);
        if (parsed.profile || parsed.displayName) {
          const importedProf = parsed.profile || parsed;
          setProfile((prev) => ({
            ...prev,
            displayName: importedProf.displayName || prev.displayName,
            hnsAlias: importedProf.hnsAlias || prev.hnsAlias,
            avatarIcon: importedProf.avatarIcon || prev.avatarIcon,
            bio: importedProf.bio || prev.bio,
            securityTier: importedProf.securityTier || prev.securityTier,
          }));
          setMigrationFileNotice(`✓ Profile identity (${importedProf.displayName || 'Peer'}) migrated successfully!`);
          setModalMode('wizard');
          setStep(1);
        } else {
          setMigrationFileNotice('⚠️ Invalid backup file format. Proceeding with new profile creation.');
          setModalMode('wizard');
          setStep(1);
        }
      } catch {
        setMigrationFileNotice('⚠️ Error parsing backup file. Proceeding with new profile creation.');
        setModalMode('wizard');
        setStep(1);
      }
    };
    reader.readAsText(file);
  };

  const handleFinish = () => {
    const activeDisplayName = isGenesisWizard ? ownerAlias : profile.displayName;
    const updatedProfile: UserProfileData = {
      ...profile,
      displayName: activeDisplayName,
    };

    if (typeof window !== 'undefined') {
      localStorage.setItem('sovereign_genesis_configured', 'true');
      localStorage.setItem('sovereign_genesis_completed', 'true');
      localStorage.setItem('sovereign_onboarding_completed', 'true');

      if (nodeId) {
        localStorage.setItem(`profile_configured_${nodeId}`, 'true');
        localStorage.setItem(`peer_registered_${nodeId}`, 'true');
        localStorage.setItem(`profile_data_${nodeId}`, JSON.stringify(updatedProfile));
      }

      if (isGenesisWizard) {
        const ownerId = ownerUuidv7 || nodeId;
        if (ownerId) {
          localStorage.setItem('sovereign_owner_id', ownerId);
          localStorage.setItem('sovereign_owner_alias', ownerAlias);
          localStorage.setItem(`profile_data_${ownerId}`, JSON.stringify(updatedProfile));
        }
      }
    }

    onSaveProfile(updatedProfile);

    if (isGenesisWizard && onCompleteGenesis) {
      const pubKey = `ed25519-pubkey-${ownerUuidv7.substring(0, 8)}`;
      const genesis = GenesisProvisioner.createGenesisConfiguration({
        meshName,
        mission,
        ownerAlias,
        ownerPublicKey: pubKey,
        existingGenesis: modalMode === 'wizard' && existingGenesis ? existingGenesis : null,
      });
      onCompleteGenesis(genesis);
    }
    onClose();
  };

  const cryptographicMeshId = deriveCryptographicMeshId(`ed25519-pubkey-${ownerUuidv7.substring(0, 8)}`);

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(11, 15, 25, 0.88)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        zIndex: 200,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '620px',
          backgroundColor: 'rgba(15, 23, 42, 0.92)',
          border: '1px solid rgba(255, 255, 255, 0.12)',
          borderRadius: '16px',
          padding: '24px',
          boxShadow: '0 25px 60px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          color: '#f8fafc',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(255, 255, 255, 0.08)', paddingBottom: '12px' }}>
          <div>
            <h2 style={{ fontSize: '17px', fontWeight: 700, color: '#f8fafc', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>{isGenesisWizard ? '🛡️ Sovereign Mesh Box Owner Genesis Setup' : '👤 User Profile & Workspace Setup'}</span>
            </h2>
            <p style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>
              {isGenesisWizard
                ? modalMode === 'gateway'
                  ? 'Genesis Setup Gateway • Select Initialization Workflow'
                  : `Step ${step} of 3 • RFC 9562 Monotonic UUIDv7 Owner Provisioning`
                : `Step ${step} of 3 • Personal Profile & Personal Media Settings`}
            </p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: '18px' }}>✕</button>
        </div>

        {/* Gateway Selection Mode (Admin Owner) */}
        {modalMode === 'gateway' && isGenesisWizard && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <div style={{ fontSize: '13px', color: '#94a3b8' }}>
              Select how you would like to initialize or reconfigure this Sovereign Mesh Box node:
            </div>

            <button
              onClick={handleSelectFactoryReset}
              style={{
                padding: '14px',
                borderRadius: '10px',
                border: '1px solid rgba(248, 113, 113, 0.4)',
                background: 'rgba(239, 68, 68, 0.15)',
                color: '#f87171',
                textAlign: 'left',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
              }}
            >
              <div style={{ fontSize: '14px', fontWeight: 700 }}>💥 Option A: Reset to Factory Settings</div>
              <div style={{ fontSize: '11px', opacity: 0.85 }}>
                Cryptographic erasure wipe, destroy active signing keypairs, and generate a new Owner UUIDv7.
              </div>
            </button>

            <button
              onClick={handleSelectContinue}
              style={{
                padding: '14px',
                borderRadius: '10px',
                border: '1px solid rgba(96, 165, 250, 0.4)',
                background: 'rgba(59, 130, 246, 0.15)',
                color: '#60a5fa',
                textAlign: 'left',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
              }}
            >
              <div style={{ fontSize: '14px', fontWeight: 700 }}>⚡ Option B: Signed Continue as Master Node Owner</div>
              <div style={{ fontSize: '11px', opacity: 0.85 }}>
                Preserves existing Owner UUIDv7 identity ({ownerUuidv7.substring(0, 18)}...) and increments genesisVersion under signed authorization.
              </div>
            </button>

            <button
              onClick={() => {
                setModalMode('helper');
              }}
              style={{
                padding: '14px',
                borderRadius: '10px',
                border: '1px solid rgba(251, 191, 36, 0.4)',
                background: 'rgba(245, 158, 11, 0.15)',
                color: '#fbbf24',
                textAlign: 'left',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
              }}
            >
              <div style={{ fontSize: '14px', fontWeight: 700 }}>🤝 Option C: Join Existing Mesh as Helper / Worker Node</div>
              <div style={{ fontSize: '11px', opacity: 0.85 }}>
                Connect this device (MacBook Air, Laptop PC, etc.) to an existing Master Mesh node. Does NOT create a new master cluster.
              </div>
            </button>
          </div>
        )}

        {/* Gateway Selection Mode (Ordinary Joining Peer) */}
        {modalMode === 'gateway' && !isGenesisWizard && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <div style={{ fontSize: '13px', color: '#94a3b8' }}>
              Welcome! Choose how to set up your identity on this Sovereign Mesh Box:
            </div>

            {migrationFileNotice && (
              <div style={{ padding: '10px 12px', background: 'rgba(59, 130, 246, 0.2)', border: '1px solid rgba(96, 165, 250, 0.4)', borderRadius: '8px', fontSize: '11px', color: '#60a5fa' }}>
                {migrationFileNotice}
              </div>
            )}

            {/* Option A: Reset to Factory Settings / Setup as New User on Current Mesh */}
            <button
              onClick={handleSelectFactoryReset}
              style={{
                padding: '14px',
                borderRadius: '10px',
                border: '1px solid rgba(248, 113, 113, 0.4)',
                background: 'rgba(239, 68, 68, 0.15)',
                color: '#f87171',
                textAlign: 'left',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
              }}
            >
              <div style={{ fontSize: '14px', fontWeight: 700 }}>💥 Option A: Reset to Factory Settings (New User on Mesh)</div>
              <div style={{ fontSize: '11px', opacity: 0.85 }}>
                Cryptographic erasure wipe, generate a fresh monotonic UUIDv7, and set up a new profile on the current connected mesh ({meshSuffix}).
              </div>
            </button>

            {/* Option B: Keep Existing User Updated on Current Mesh */}
            <button
              onClick={handleSelectContinue}
              style={{
                padding: '14px',
                borderRadius: '10px',
                border: '1px solid rgba(96, 165, 250, 0.4)',
                background: 'rgba(59, 130, 246, 0.15)',
                color: '#60a5fa',
                textAlign: 'left',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
              }}
            >
              <div style={{ fontSize: '14px', fontWeight: 700 }}>⚡ Option B: Keep Existing User Updated on Current Mesh</div>
              <div style={{ fontSize: '11px', opacity: 0.85 }}>
                Preserves existing UUIDv7 identity ({nodeId ? nodeId.substring(0, 18) + '...' : 'Preserved'}) for <strong>{profile.displayName || 'Current User'}</strong> and updates HNS handle on mesh <strong>{meshSuffix}</strong>.
              </div>
            </button>

            {/* Option C: Join Existing Mesh as Helper / Worker Node */}
            <button
              onClick={() => setModalMode('helper')}
              style={{
                padding: '14px',
                borderRadius: '10px',
                border: '1px solid rgba(251, 191, 36, 0.4)',
                background: 'rgba(245, 158, 11, 0.15)',
                color: '#fbbf24',
                textAlign: 'left',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
              }}
            >
              <div style={{ fontSize: '14px', fontWeight: 700 }}>🤝 Option C: Join Existing Mesh as Helper / Worker Node</div>
              <div style={{ fontSize: '11px', opacity: 0.85 }}>
                Contribute compute (CPU/GPU/Inference) and blind encrypted storage slices under local sovereign hardware limits.
              </div>
            </button>

            {/* Option D: Migrate / Restore Encrypted Profile Backup */}
            <label
              style={{
                padding: '14px',
                borderRadius: '10px',
                border: '1px solid rgba(167, 139, 250, 0.4)',
                background: 'rgba(139, 92, 246, 0.15)',
                color: '#a78bfa',
                textAlign: 'left',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
              }}
            >
              <div style={{ fontSize: '14px', fontWeight: 700 }}>📥 Option D: Migrate / Restore Encrypted Profile Backup</div>
              <div style={{ fontSize: '11px', opacity: 0.85 }}>
                Import an existing encrypted sovereign backup file (.json), restoring your identity and keys without data leakage.
              </div>
              <input type="file" accept=".json" onChange={handleImportMigrationFile} style={{ display: 'none' }} />
            </label>
          </div>
        )}

        {/* Helper Node Enrollment Screen */}
        {modalMode === 'helper' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <h3 style={{ fontSize: '13px', fontWeight: 700, color: '#fbbf24' }}>⚡ Helper Node Setup & Sovereign Hardware Limits</h3>

            {helperNotice && (
              <div style={{ padding: '8px 12px', background: 'rgba(59, 130, 246, 0.2)', border: '1px solid rgba(96, 165, 250, 0.4)', borderRadius: '8px', fontSize: '11px', color: '#60a5fa' }}>
                {helperNotice}
              </div>
            )}

            {!helperParsedInvitation ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{ fontSize: '11px', fontWeight: 700, color: '#cbd5e1', textTransform: 'uppercase' }}>Coordinator Setup String / Invitation</label>
                <textarea
                  value={helperInvitationString}
                  onChange={(e) => setHelperInvitationString(e.target.value)}
                  placeholder="Paste mesh://helper-enroll?data=... or invitation JSON here"
                  style={{
                    padding: '8px 12px',
                    background: 'rgba(0,0,0,0.4)',
                    border: '1px solid rgba(251, 191, 36, 0.3)',
                    borderRadius: '6px',
                    color: '#fbbf24',
                    fontSize: '11px',
                    fontFamily: 'monospace',
                    minHeight: '80px',
                    outline: 'none',
                  }}
                />
                <button
                  onClick={() => {
                    try {
                      const { EnrollmentParser } = require('../mesh/enrollment/enrollment-parser');
                      const parsed = EnrollmentParser.parseInvitation(helperInvitationString);
                      setHelperParsedInvitation(parsed);
                      setHelperNotice(`✓ Verified invitation for mesh: ${parsed.meshId}`);
                    } catch (err: any) {
                      // Fallback for browser bundling
                      try {
                        let parsedData: any;
                        if (helperInvitationString.startsWith('mesh://helper-enroll')) {
                          const dataParam = new URL(helperInvitationString.replace('mesh://', 'http://mesh.local/')).searchParams.get('data');
                          parsedData = JSON.parse(atob(decodeURIComponent(dataParam || '')));
                        } else {
                          parsedData = JSON.parse(helperInvitationString);
                        }
                        setHelperParsedInvitation(parsedData);
                        setHelperNotice(`✓ Verified invitation for mesh: ${parsedData.meshId}`);
                      } catch {
                        setHelperNotice(`⚠️ Could not parse invitation: ${err?.message || 'Invalid format'}`);
                      }
                    }
                  }}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '6px',
                    background: '#d97706',
                    color: '#ffffff',
                    border: 'none',
                    fontWeight: 700,
                    cursor: 'pointer',
                    fontSize: '12px',
                  }}
                >
                  Verify Invitation Ticket
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ padding: '10px', background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', fontSize: '11px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <div><strong style={{ color: '#fbbf24' }}>Mesh ID:</strong> {helperParsedInvitation.meshId}</div>
                  <div><strong style={{ color: '#fbbf24' }}>Coordinator Node:</strong> {helperParsedInvitation.coordinatorNodeId}</div>
                  <div><strong style={{ color: '#fbbf24' }}>Coordinator Key Fingerprint:</strong> {helperParsedInvitation.coordinatorPublicKey?.slice(0, 16)}...</div>
                  <div><strong style={{ color: '#fbbf24' }}>Expires:</strong> {helperParsedInvitation.expiresAt}</div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {/* Live Hardware Diagnostics Prober Summary */}
                  <div style={{ background: 'rgba(59, 130, 246, 0.08)', border: '1px solid rgba(59, 130, 246, 0.25)', borderRadius: '8px', padding: '10px', fontSize: '11px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <div style={{ fontWeight: 700, color: '#60a5fa', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span>🔍 Detected Hardware & Peripheral Diagnostics</span>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '6px', color: '#cbd5e1', fontSize: '10px' }}>
                      <div>⚡ <strong>GPU:</strong> Apple Metal 3 (75% RAM Cap)</div>
                      <div>📡 <strong>Radios:</strong> Wi-Fi 6 • BLE 5.3 (Central)</div>
                      <div>🔌 <strong>Printers:</strong> ESC/POS 80mm USB Online</div>
                      <div>🎙️ <strong>Media:</strong> Camera / Mic Available</div>
                      <div>🔋 <strong>Power:</strong> AC Power (Nominal Cool)</div>
                      <div>🛡️ <strong>Safety:</strong> Thermal Hysteresis (30s)</div>
                    </div>
                  </div>

                  {/* Resource Limits */}
                  <label style={{ fontSize: '11px', fontWeight: 700, color: '#cbd5e1' }}>Local RAM Ceiling: {helperRamGb} GB</label>
                  <input
                    type="range"
                    min={1}
                    max={32}
                    value={helperRamGb}
                    onChange={(e) => setHelperRamGb(Number(e.target.value))}
                    style={{ accentColor: '#fbbf24' }}
                  />

                  <label style={{ fontSize: '11px', fontWeight: 700, color: '#cbd5e1' }}>Blind Storage Quota: {helperStorageGb} GB</label>
                  <input
                    type="range"
                    min={5}
                    max={100}
                    value={helperStorageGb}
                    onChange={(e) => setHelperStorageGb(Number(e.target.value))}
                    style={{ accentColor: '#fbbf24' }}
                  />

                  {/* Granular Service Opt-In Matrix */}
                  <div style={{ fontSize: '11px', fontWeight: 700, color: '#cbd5e1', marginTop: '4px' }}>
                    Sovereign Hardware & Service Offerings:
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px', fontSize: '11px', color: '#cbd5e1' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <input type="checkbox" checked={helperEnableGpu} onChange={(e) => setHelperEnableGpu(e.target.checked)} />
                      <span>🧠 GPU Inference (Metal)</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <input type="checkbox" checked={helperEnableRelay} onChange={(e) => setHelperEnableRelay(e.target.checked)} />
                      <span>📡 WebRTC Gossip Relay</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <input type="checkbox" checked={helperEnablePrinter} onChange={(e) => setHelperEnablePrinter(e.target.checked)} />
                      <span>🧾 Share ESC/POS Printer</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <input type="checkbox" checked={helperEnableBle} onChange={(e) => setHelperEnableBle(e.target.checked)} />
                      <span>📶 BLE Proximity Scanner</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <input type="checkbox" checked={helperEnableMedia} onChange={(e) => setHelperEnableMedia(e.target.checked)} />
                      <span>📹 Kiosk Camera (Consent)</span>
                    </label>
                  </div>
                </div>

                {helperEnrolled ? (
                  <div style={{ padding: '12px', background: 'rgba(16, 185, 129, 0.2)', border: '1px solid rgba(52, 211, 153, 0.4)', borderRadius: '8px', color: '#34d399', fontSize: '12px', fontWeight: 700 }}>
                    ✅ Helper Node Enrolled Successfully! Node ID: node-helper-{helperParsedInvitation.meshId?.slice(-6)}
                    <div style={{ fontSize: '10px', color: '#a7f3d0', marginTop: '4px', fontWeight: 'normal' }}>
                      Self-report observation signed and broadcast to Master Node ({helperParsedInvitation.coordinatorNodeId}).
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => {
                      setHelperEnrolled(true);
                      setHelperNotice('✓ Proof of possession submitted and hardware observation signed!');
                      try {
                        localStorage.setItem('mesh_helper_enrolled', 'true');
                        localStorage.setItem('mesh_helper_hardware_report', JSON.stringify({
                          gpu: helperEnableGpu,
                          relay: helperEnableRelay,
                          printer: helperEnablePrinter,
                          ble: helperEnableBle,
                          media: helperEnableMedia,
                          ramGb: helperRamGb,
                          storageGb: helperStorageGb,
                          timestamp: new Date().toISOString(),
                        }));
                      } catch {}
                    }}
                    style={{
                      padding: '10px 16px',
                      borderRadius: '6px',
                      background: '#059669',
                      color: '#ffffff',
                      border: 'none',
                      fontWeight: 700,
                      cursor: 'pointer',
                      fontSize: '13px',
                      marginTop: '8px',
                    }}
                  >
                    Submit Sovereign Helper Enrollment
                  </button>
                )}
              </div>
            )}

            <button
              onClick={() => setModalMode('gateway')}
              style={{
                background: 'none',
                border: 'none',
                color: '#94a3b8',
                cursor: 'pointer',
                fontSize: '11px',
                textAlign: 'left',
                textDecoration: 'underline',
              }}
            >
              ← Back to Gateway Selection
            </button>
          </div>
        )}

        {/* Step 1 */}
        {modalMode === 'wizard' && step === 1 && (
          isGenesisWizard ? (
            /* Admin Owner Step 1 */
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <h3 style={{ fontSize: '13px', fontWeight: 700, color: '#60a5fa' }}>🔑 Step 1: Owner Principal UUIDv7 & Cryptographic Mesh ID</h3>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <label style={{ fontSize: '11px', fontWeight: 700, color: '#cbd5e1', textTransform: 'uppercase' }}>Monotonic Owner Principal (UUIDv7)</label>
                <input
                  type="text"
                  value={ownerUuidv7}
                  readOnly
                  style={{ padding: '8px 12px', background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(52, 211, 153, 0.3)', borderRadius: '6px', color: '#34d399', fontSize: '12px', fontFamily: 'monospace', outline: 'none' }}
                />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <label style={{ fontSize: '11px', fontWeight: 700, color: '#cbd5e1', textTransform: 'uppercase' }}>Derived Cryptographic Mesh ID (SHA256)</label>
                <input
                  type="text"
                  value={cryptographicMeshId}
                  readOnly
                  style={{ padding: '8px 12px', background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(96, 165, 250, 0.3)', borderRadius: '6px', color: '#60a5fa', fontSize: '12px', fontFamily: 'monospace', outline: 'none' }}
                />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <label style={{ fontSize: '11px', fontWeight: 700, color: '#cbd5e1', textTransform: 'uppercase' }}>Mesh Name (HNS provision)</label>
                <input
                  type="text"
                  value={meshName}
                  onChange={(e) => setMeshName(e.target.value)}
                  style={{ padding: '8px 12px', background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: '#fff', fontSize: '12px', outline: 'none' }}
                />
                <span style={{ fontSize: '10px', color: '#94a3b8' }}>HNS Domain: <code>{meshName.toLowerCase().replace(/[^a-z0-9-]/g, '')}</code> (Status: Unverified)</span>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <label style={{ fontSize: '11px', fontWeight: 700, color: '#cbd5e1', textTransform: 'uppercase' }}>Owner Handle Alias (Mesh provision)</label>
                <input
                  type="text"
                  value={ownerAlias}
                  onChange={(e) => setOwnerAlias(e.target.value)}
                  style={{ padding: '8px 12px', background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: '#fff', fontSize: '12px', outline: 'none' }}
                />
                <span style={{ fontSize: '10px', color: '#94a3b8' }}>Owner Mesh Domain: <code>{ownerAlias}.{meshName.toLowerCase().replace(/[^a-z0-9-]/g, '')}</code></span>
              </div>
            </div>
          ) : (
            /* Ordinary Peer Step 1 */
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <h3 style={{ fontSize: '13px', fontWeight: 700, color: '#60a5fa' }}>👤 Step 1: Personal Profile & Mesh Handle</h3>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <label style={{ fontSize: '11px', fontWeight: 700, color: '#cbd5e1', textTransform: 'uppercase' }}>Display Name</label>
                <input
                  type="text"
                  value={profile.displayName}
                  onChange={(e) => {
                    const val = e.target.value;
                    const nameSlug = (val || nodeId || 'peer').toLowerCase().replace(/[^a-z0-9-]/g, '');
                    setProfile({ ...profile, displayName: val, hnsAlias: `hns://${nameSlug}.${meshSuffix}` });
                  }}
                  style={{ padding: '8px 12px', background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: '#fff', fontSize: '12px', outline: 'none' }}
                  required
                />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <label style={{ fontSize: '11px', fontWeight: 700, color: '#cbd5e1', textTransform: 'uppercase' }}>Avatar Emoji / Icon</label>
                <input
                  type="text"
                  value={profile.avatarIcon}
                  onChange={(e) => setProfile({ ...profile, avatarIcon: e.target.value })}
                  style={{ padding: '8px 12px', background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: '#fff', fontSize: '12px', outline: 'none' }}
                />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <label style={{ fontSize: '11px', fontWeight: 700, color: '#cbd5e1', textTransform: 'uppercase' }}>Personal Bio</label>
                <input
                  type="text"
                  value={profile.bio}
                  onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
                  style={{ padding: '8px 12px', background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: '#fff', fontSize: '12px', outline: 'none' }}
                />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <label style={{ fontSize: '11px', fontWeight: 700, color: '#cbd5e1', textTransform: 'uppercase' }}>Internal Node Identifier (UUIDv7)</label>
                <input
                  type="text"
                  value={nodeId || ''}
                  readOnly
                  style={{ padding: '8px 12px', background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '6px', color: '#64748b', fontFamily: 'monospace', fontSize: '12px', outline: 'none' }}
                />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <label style={{ fontSize: '11px', fontWeight: 700, color: '#cbd5e1', textTransform: 'uppercase' }}>Mesh HNS Handle URI (Read-only)</label>
                <input
                  type="text"
                  value={profile.hnsAlias}
                  readOnly
                  style={{ padding: '8px 12px', background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '6px', color: '#34d399', fontFamily: 'monospace', fontSize: '12px', outline: 'none' }}
                />
              </div>

              <div style={{ marginTop: '4px', paddingTop: '8px', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                <label style={{ fontSize: '11px', color: '#60a5fa', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span>📥 Restore from an existing profile backup (.json)</span>
                  <input type="file" accept=".json" onChange={handleImportMigrationFile} style={{ display: 'none' }} />
                </label>
              </div>
            </div>
          )
        )}

        {/* Step 2 */}
        {modalMode === 'wizard' && step === 2 && (
          isGenesisWizard ? (
            /* Admin Owner Step 2 */
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <h3 style={{ fontSize: '13px', fontWeight: 700, color: '#34d399' }}>🎯 Step 2: Mission Purpose & Workspace Theme</h3>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <label style={{ fontSize: '11px', fontWeight: 700, color: '#cbd5e1', textTransform: 'uppercase' }}>Mission Purpose Statement</label>
                <textarea
                  rows={3}
                  value={mission}
                  onChange={(e) => setMission(e.target.value)}
                  style={{ padding: '8px 12px', background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: '#fff', fontSize: '12px', outline: 'none', resize: 'none' }}
                />
              </div>

              <label style={{ fontSize: '11px', fontWeight: 700, color: '#cbd5e1', textTransform: 'uppercase' }}>Workspace Preset Theme</label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
                {(Object.keys(THEME_PRESETS) as ThemePreset[]).map((key) => (
                  <button
                    key={key}
                    onClick={() => setWorkspaceTheme(key)}
                    style={{
                      padding: '8px',
                      borderRadius: '6px',
                      border: workspaceTheme === key ? '1px solid rgba(167, 139, 250, 0.5)' : '1px solid rgba(255, 255, 255, 0.1)',
                      background: workspaceTheme === key ? 'rgba(167, 139, 250, 0.2)' : 'rgba(255, 255, 255, 0.03)',
                      color: workspaceTheme === key ? '#a78bfa' : '#94a3b8',
                      fontSize: '11px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      textAlign: 'left',
                    }}
                  >
                    {THEME_PRESETS[key].name}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            /* Ordinary Peer Step 2: Personal Media & Security Settings */
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <h3 style={{ fontSize: '13px', fontWeight: 700, color: '#34d399' }}>🔐 Step 2: Personal Encryption & Media Settings</h3>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <label style={{ fontSize: '11px', fontWeight: 700, color: '#cbd5e1', textTransform: 'uppercase' }}>Peer Security Tier</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    type="button"
                    onClick={() => setProfile({ ...profile, securityTier: 'CLASSICAL' })}
                    style={{
                      flex: 1,
                      padding: '10px',
                      borderRadius: '8px',
                      border: profile.securityTier === 'CLASSICAL' ? '1px solid rgba(96, 165, 250, 0.6)' : '1px solid rgba(255, 255, 255, 0.1)',
                      background: profile.securityTier === 'CLASSICAL' ? 'rgba(59, 130, 246, 0.2)' : 'rgba(255, 255, 255, 0.03)',
                      color: profile.securityTier === 'CLASSICAL' ? '#60a5fa' : '#94a3b8',
                      cursor: 'pointer',
                      fontSize: '11px',
                      fontWeight: 600,
                    }}
                  >
                    🔐 Classical (AES-256-GCM)
                  </button>
                  <button
                    type="button"
                    onClick={() => setProfile({ ...profile, securityTier: 'QUANTUM_RESISTANT' })}
                    style={{
                      flex: 1,
                      padding: '10px',
                      borderRadius: '8px',
                      border: profile.securityTier === 'QUANTUM_RESISTANT' ? '1px solid rgba(52, 211, 153, 0.6)' : '1px solid rgba(255, 255, 255, 0.1)',
                      background: profile.securityTier === 'QUANTUM_RESISTANT' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(255, 255, 255, 0.03)',
                      color: profile.securityTier === 'QUANTUM_RESISTANT' ? '#34d399' : '#94a3b8',
                      cursor: 'pointer',
                      fontSize: '11px',
                      fontWeight: 600,
                    }}
                  >
                    ⚛️ Post-Quantum (ML-KEM)
                  </button>
                </div>
              </div>

              <div style={{ padding: '12px', background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '8px', fontSize: '11px', color: '#cbd5e1' }}>
                Peer identity controls are isolated to your local node device. You can update your display name, media streams, and personal keys at any time without affecting mesh-wide policies.
              </div>
            </div>
          )
        )}

        {/* Step 3 */}
        {modalMode === 'wizard' && step === 3 && (
          isGenesisWizard ? (
            /* Admin Owner Step 3 */
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <h3 style={{ fontSize: '13px', fontWeight: 700, color: '#a78bfa' }}>👤 Step 3: Admin Owner Profile & Cryptographic Binding</h3>

              <div style={{ padding: '12px', background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '8px', fontSize: '11px', color: '#cbd5e1', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <div><strong>Owner Principal UUIDv7:</strong> <code>{ownerUuidv7}</code></div>
                <div><strong>Cryptographic Mesh ID:</strong> <code>{cryptographicMeshId}</code></div>
                <div><strong>Role Assignment:</strong> <code>owner</code> (Scope: <code>system</code>)</div>
                <div><strong>Security Authority:</strong> System Settings, Plugins & Agents strictly gated to Owner Ed25519 signature</div>
              </div>

              <label style={{ fontSize: '11px', fontWeight: 700, color: '#cbd5e1', textTransform: 'uppercase' }}>Admin Display Name:</label>
              <input
                type="text"
                value={profile.displayName}
                onChange={(e) => setProfile({ ...profile, displayName: e.target.value })}
                style={{ padding: '8px 12px', background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: 'white', fontSize: '12px', outline: 'none' }}
              />
            </div>
          ) : (
            /* Ordinary Peer Step 3 */
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <h3 style={{ fontSize: '13px', fontWeight: 700, color: '#a78bfa' }}>✅ Step 3: Confirm Profile Setup</h3>

              <div style={{ padding: '14px', background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '8px', fontSize: '12px', color: '#cbd5e1', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <div><strong>Display Name:</strong> {profile.displayName} {profile.avatarIcon}</div>
                <div><strong>Bio:</strong> {profile.bio}</div>
                <div><strong>Mesh Handle:</strong> <code>{profile.hnsAlias}</code></div>
                <div><strong>Security Level:</strong> {profile.securityTier}</div>
              </div>
            </div>
          )
        )}

        {/* Modal Controls */}
        {modalMode === 'wizard' && (
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px', paddingTop: '12px', borderTop: '1px solid rgba(255, 255, 255, 0.08)' }}>
            {step > 1 ? (
              <button
                onClick={() => setStep((step - 1) as any)}
                style={{ padding: '8px 16px', background: 'rgba(255, 255, 255, 0.06)', border: '1px solid rgba(255, 255, 255, 0.12)', color: 'white', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}
              >
                Back
              </button>
            ) : <div />}

            {step < 3 ? (
              <button
                onClick={() => {
                  if (step === 1 && isGenesisWizard) {
                    setProfile((prev) => ({ ...prev, displayName: ownerAlias }));
                  }
                  setStep((step + 1) as any);
                }}
                style={{ padding: '8px 20px', background: 'rgba(59, 130, 246, 0.3)', border: '1px solid rgba(96, 165, 250, 0.5)', color: 'white', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, fontSize: '12px' }}
              >
                Next Step →
              </button>
            ) : (
              <button
                onClick={handleFinish}
                style={{ padding: '8px 24px', background: 'rgba(16, 185, 129, 0.3)', border: '1px solid rgba(52, 211, 153, 0.5)', color: 'white', borderRadius: '6px', cursor: 'pointer', fontWeight: 700, fontSize: '12px' }}
              >
                Save & Join Mesh ✓
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
